import { useEffect, useState } from "react";
import { db } from "./firebase";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";

// ── Portion types (Precision Nutrition hand-portion system) ──────────────────
export const PORTIONS = [
  { key: "protein",  label: "Protein",  emoji: "🤚", unit: "palm",        color: "#fde68a", border: "#f59e0b", text: "#92400e" },
  { key: "veggies",  label: "Veggies",  emoji: "👊", unit: "fist",        color: "#bbf7d0", border: "#22c55e", text: "#14532d" },
  { key: "carbs",    label: "Carbs",    emoji: "🫲", unit: "cupped hand", color: "#bfdbfe", border: "#3b82f6", text: "#1e3a5f" },
  { key: "fats",     label: "Fats",     emoji: "👍", unit: "thumb",       color: "#f5d0fe", border: "#a855f7", text: "#581c87" },
];

const MEALS = [
  { key: "breakfast", label: "Breakfast", emoji: "🌅" },
  { key: "lunch",     label: "Lunch",     emoji: "☀️" },
  { key: "dinner",    label: "Dinner",    emoji: "🌙" },
  { key: "snacks",    label: "Snacks",    emoji: "🍎" },
];

const DEFAULT_TARGETS = { protein: 4, veggies: 4, carbs: 3, fats: 3 };

function emptyMeals() {
  const obj = {};
  MEALS.forEach(({ key }) => {
    obj[key] = { protein: 0, veggies: 0, carbs: 0, fats: 0 };
  });
  return obj;
}

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

async function loadPortionLog(uid, date) {
  const snap = await getDoc(doc(db, "portionLogs", `${uid}_${date}`));
  return snap.exists() ? snap.data() : { meals: emptyMeals() };
}

async function savePortionLog(uid, date, data) {
  await setDoc(
    doc(db, "portionLogs", `${uid}_${date}`),
    { uid, date, ...data, updatedAt: serverTimestamp() },
    { merge: true }
  );
}

async function loadTargets(uid) {
  const snap = await getDoc(doc(db, "users", uid));
  return snap.exists() ? (snap.data().portionTargets || DEFAULT_TARGETS) : DEFAULT_TARGETS;
}

async function saveTargets(uid, targets) {
  await setDoc(doc(db, "users", uid), { portionTargets: targets }, { merge: true });
}

// ── Component ────────────────────────────────────────────────────────────────
export default function HandPortionLog({ uid, date = todayStr() }) {
  const [meals, setMeals] = useState(emptyMeals());
  const [targets, setTargets] = useState(DEFAULT_TARGETS);
  const [editingTargets, setEditingTargets] = useState(false);
  const [draftTargets, setDraftTargets] = useState(DEFAULT_TARGETS);
  const [expandedMeal, setExpandedMeal] = useState(null);
  const [saving, setSaving] = useState(false);

  // Load targets + today's log
  useEffect(() => {
    if (!uid) return;
    loadTargets(uid).then(setTargets);
  }, [uid]);

  useEffect(() => {
    if (!uid || !date) return;
    setMeals(emptyMeals());
    loadPortionLog(uid, date).then((data) => {
      setMeals(data.meals || emptyMeals());
    });
  }, [uid, date]);

  // Compute daily totals
  const totals = PORTIONS.reduce((acc, { key }) => {
    acc[key] = MEALS.reduce((sum, { key: mk }) => sum + (meals[mk]?.[key] || 0), 0);
    return acc;
  }, {});

  async function updateMealPortion(mealKey, portionKey, delta) {
    const current = meals[mealKey]?.[portionKey] || 0;
    const next = Math.max(0, current + delta);
    const updated = {
      ...meals,
      [mealKey]: { ...meals[mealKey], [portionKey]: next },
    };
    setMeals(updated);
    setSaving(true);
    try {
      await savePortionLog(uid, date, { meals: updated });
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveTargets() {
    const cleaned = {};
    PORTIONS.forEach(({ key }) => {
      cleaned[key] = Math.max(0, parseInt(draftTargets[key], 10) || 0);
    });
    setTargets(cleaned);
    setEditingTargets(false);
    await saveTargets(uid, cleaned);
  }

  return (
    <div>
      {/* ── Daily Summary ── */}
      <div style={{
        background: "#f8fbff", borderRadius: "18px", padding: "16px",
        border: "2px solid #bfdbfe", marginBottom: "14px",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
          <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "15px" }}>
            Daily Progress
          </div>
          <button
            type="button"
            onClick={() => { setDraftTargets({ ...targets }); setEditingTargets(true); }}
            style={{
              padding: "4px 10px", borderRadius: "10px", border: "2px solid #bfdbfe",
              background: "#f5faff", color: "#4b6b8a", fontSize: "12px",
              fontWeight: "bold", cursor: "pointer",
            }}
          >
            Edit Targets
          </button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
          {PORTIONS.map(({ key, label, emoji, unit, color, border, text }) => {
            const got = totals[key];
            const target = targets[key] || 0;
            const pct = target > 0 ? Math.min(100, (got / target) * 100) : 0;
            const done = got >= target && target > 0;
            return (
              <div key={key} style={{
                background: color, borderRadius: "14px", padding: "12px",
                border: `2px solid ${border}`,
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "6px" }}>
                  <span style={{ fontSize: "18px" }}>{emoji}</span>
                  <span style={{ fontWeight: "bold", color: text, fontSize: "13px" }}>{label}</span>
                  {done && <span style={{ fontSize: "12px" }}>✅</span>}
                </div>
                <div style={{ fontSize: "20px", fontWeight: "bold", color: text, marginBottom: "4px" }}>
                  {got} <span style={{ fontSize: "13px", fontWeight: "normal" }}>/ {target} {unit}s</span>
                </div>
                {/* Progress bar */}
                <div style={{ height: "6px", background: "rgba(255,255,255,0.5)", borderRadius: "999px", overflow: "hidden" }}>
                  <div style={{
                    height: "100%", width: `${pct}%`,
                    background: border, borderRadius: "999px",
                    transition: "width 0.3s ease",
                  }} />
                </div>
              </div>
            );
          })}
        </div>

        {saving && (
          <div style={{ color: "#6b8cb0", fontSize: "11px", textAlign: "right", marginTop: "6px" }}>Saving…</div>
        )}
      </div>

      {/* ── Meal Rows ── */}
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        {MEALS.map(({ key, label, emoji }) => {
          const meal = meals[key] || {};
          const isExpanded = expandedMeal === key;
          const mealTotal = PORTIONS.reduce((s, { key: pk }) => s + (meal[pk] || 0), 0);

          return (
            <div key={key} style={{
              background: "#f8fbff", borderRadius: "16px",
              border: "2px solid #bfdbfe", overflow: "hidden",
            }}>
              {/* Header */}
              <button
                type="button"
                onClick={() => setExpandedMeal(isExpanded ? null : key)}
                style={{
                  width: "100%", display: "flex", alignItems: "center", gap: "10px",
                  padding: "12px 14px", background: "none", border: "none",
                  cursor: "pointer", textAlign: "left",
                }}
              >
                <span style={{ fontSize: "20px" }}>{emoji}</span>
                <span style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "15px", flex: 1 }}>
                  {label}
                </span>
                {/* Mini portion chips when collapsed */}
                {!isExpanded && mealTotal > 0 && (
                  <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
                    {PORTIONS.map(({ key: pk, emoji: pe, color, border }) => {
                      const count = meal[pk] || 0;
                      if (count === 0) return null;
                      return (
                        <span key={pk} style={{
                          padding: "2px 7px", borderRadius: "999px",
                          background: color, border: `1.5px solid ${border}`,
                          fontSize: "11px", fontWeight: "bold", color: "#1e3a5f",
                        }}>
                          {pe} {count}
                        </span>
                      );
                    })}
                  </div>
                )}
                <span style={{ color: "#93c5fd", fontSize: "13px" }}>{isExpanded ? "▲" : "▼"}</span>
              </button>

              {/* Expanded portion controls */}
              {isExpanded && (
                <div style={{ padding: "0 14px 14px" }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                    {PORTIONS.map(({ key: pk, label: pl, emoji: pe, unit, color, border, text }) => {
                      const count = meal[pk] || 0;
                      return (
                        <div key={pk} style={{
                          background: color, borderRadius: "14px", padding: "10px 12px",
                          border: `2px solid ${border}`,
                          display: "flex", flexDirection: "column", gap: "6px",
                        }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                            <span style={{ fontSize: "16px" }}>{pe}</span>
                            <span style={{ fontWeight: "bold", color: text, fontSize: "12px" }}>{pl}</span>
                          </div>
                          <div style={{ fontSize: "10px", color: text, opacity: 0.7 }}>1 {unit} = 1 serving</div>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <button
                              type="button"
                              onClick={() => updateMealPortion(key, pk, -1)}
                              disabled={count === 0}
                              style={{
                                width: "30px", height: "30px", borderRadius: "999px",
                                border: `2px solid ${border}`, background: "rgba(255,255,255,0.6)",
                                color: text, fontWeight: "bold", fontSize: "16px",
                                cursor: count === 0 ? "not-allowed" : "pointer",
                                opacity: count === 0 ? 0.4 : 1,
                                display: "flex", alignItems: "center", justifyContent: "center",
                              }}
                            >−</button>
                            <span style={{ fontWeight: "bold", color: text, fontSize: "20px", minWidth: "20px", textAlign: "center" }}>
                              {count}
                            </span>
                            <button
                              type="button"
                              onClick={() => updateMealPortion(key, pk, 1)}
                              style={{
                                width: "30px", height: "30px", borderRadius: "999px",
                                border: `2px solid ${border}`, background: "rgba(255,255,255,0.6)",
                                color: text, fontWeight: "bold", fontSize: "16px",
                                cursor: "pointer",
                                display: "flex", alignItems: "center", justifyContent: "center",
                              }}
                            >+</button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Edit Targets Modal ── */}
      {editingTargets && (
        <div
          style={{
            position: "fixed", inset: 0, background: "rgba(30,58,138,0.35)",
            zIndex: 1000, display: "flex", alignItems: "flex-end", justifyContent: "center",
          }}
          onClick={() => setEditingTargets(false)}
        >
          <div
            style={{
              background: "#f5faff", borderRadius: "24px 24px 0 0", padding: "24px",
              width: "100%", maxWidth: "480px", boxSizing: "border-box",
              boxShadow: "0 -8px 32px rgba(30,58,138,0.18)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "18px", marginBottom: "4px" }}>
              Set Daily Targets
            </div>
            <p style={{ color: "#6b8cb0", fontSize: "13px", marginTop: 0, marginBottom: "18px" }}>
              How many hand portions of each food group do you aim for each day?
            </p>

            <div style={{ display: "grid", gap: "12px" }}>
              {PORTIONS.map(({ key, label, emoji, unit, color, border, text }) => (
                <div key={key} style={{
                  background: color, borderRadius: "14px", padding: "12px 14px",
                  border: `2px solid ${border}`,
                  display: "flex", alignItems: "center", gap: "12px",
                }}>
                  <span style={{ fontSize: "22px" }}>{emoji}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: "bold", color: text, fontSize: "14px" }}>{label}</div>
                    <div style={{ fontSize: "11px", color: text, opacity: 0.7 }}>per day in {unit}s</div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <button
                      type="button"
                      onClick={() => setDraftTargets((prev) => ({ ...prev, [key]: Math.max(0, (prev[key] || 0) - 1) }))}
                      style={{
                        width: "32px", height: "32px", borderRadius: "999px",
                        border: `2px solid ${border}`, background: "rgba(255,255,255,0.6)",
                        color: text, fontWeight: "bold", fontSize: "18px", cursor: "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}
                    >−</button>
                    <span style={{ fontWeight: "bold", color: text, fontSize: "22px", minWidth: "24px", textAlign: "center" }}>
                      {draftTargets[key] ?? 0}
                    </span>
                    <button
                      type="button"
                      onClick={() => setDraftTargets((prev) => ({ ...prev, [key]: (prev[key] || 0) + 1 }))}
                      style={{
                        width: "32px", height: "32px", borderRadius: "999px",
                        border: `2px solid ${border}`, background: "rgba(255,255,255,0.6)",
                        color: text, fontWeight: "bold", fontSize: "18px", cursor: "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}
                    >+</button>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ display: "flex", gap: "8px", marginTop: "20px" }}>
              <button
                type="button"
                onClick={handleSaveTargets}
                style={{
                  flex: 1, padding: "12px", borderRadius: "14px", border: "none",
                  background: "#2563eb", color: "#fff", fontWeight: "bold",
                  fontSize: "14px", cursor: "pointer",
                }}
              >
                Save Targets
              </button>
              <button
                type="button"
                onClick={() => setEditingTargets(false)}
                style={{
                  padding: "12px 16px", borderRadius: "14px",
                  border: "2px solid #bfdbfe", background: "#f5faff",
                  color: "#4b6b8a", fontWeight: "bold", fontSize: "14px", cursor: "pointer",
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
